import React from 'react';

import { isAiXstateMigrationEnabled } from '@atlaskit/editor-common/ai-xstate-migration';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ErrorScreen } from '@atlassian/generative-ai-modal/screens/Error';

import { updateAIAuditLogPluginState } from '../../../../pm-plugins/ai-audit-logs/commands';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import type { ErrorInfo } from '../../../utils/error-handling';
import { ErrorMessage, getStepName, retryEnabled } from '../../../utils/error-handling';
import { FAILURE_REASON } from '../../../utils/errors';

export function ErrorScreenWithLogic({
	errorInfo,
}: {
	errorInfo: ErrorInfo;
}): React.JSX.Element | null {
	if (isAiXstateMigrationEnabled()) {
		return <ErrorScreenWithLogicXstate errorInfo={errorInfo} />;
	}
	return <ErrorScreenWithLogicInner errorInfo={errorInfo} />;
}

// Outer guard for xstate migration experiment — checks nextSessionId before
// mounting ErrorScreenWithLogicInner so that useAnalyticsFlowEventsQueue is
// never invoked for a component that will immediately return null (which would
// fire a spurious noEventExistsToBeFired → unhandledErrorCaught event with
// errorKey: null).
// When cleaning up platform_editor_ai_xstate_migration, move this into ErrorScreenWithLogic and remove this component.
function ErrorScreenWithLogicXstate({
	errorInfo,
}: {
	errorInfo: ErrorInfo;
}): React.JSX.Element | null {
	const { api } = useContentComponentContext();
	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	if (nextSessionId) {
		return null;
	}

	return <ErrorScreenWithLogicInner errorInfo={errorInfo} />;
}

function ErrorScreenWithLogicInner({
	errorInfo,
}: {
	errorInfo: ErrorInfo;
}): React.JSX.Element | null {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const { endExperience, sendToAIExperienceMachine, editorView, appearance } =
		aiExperienceCommonData;
	const { isInDocumentStreamingEnabled } = useContentComponentContext();

	useAnalyticsFlowEventsQueue({
		stepName: getStepName(errorInfo),
		callerComponent: 'ErrorScreenWithLogic',
		attributes: {
			errorType: errorInfo.failureReason ?? FAILURE_REASON.UNKNOWN,
			sawAupViolation: errorInfo.failureReason === FAILURE_REASON.AUP_VIOLATION,
			...errorInfo,
		},
	});

	React.useEffect(() => {
		if (fg('platform_editor_ai_audit_logs_events')) {
			if (errorInfo.failureReason === FAILURE_REASON.AUP_VIOLATION) {
				const command = updateAIAuditLogPluginState({
					'aup-violation': {
						eventStatus: 'send-event',
					},
				});
				command(editorView.state, editorView.dispatch);
			}
		}
	}, [errorInfo.failureReason, editorView]);

	return (
		<ErrorScreen
			text={ErrorMessage(errorInfo)}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onCancel={() => {
				endExperience();
			}}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onRetry={() => sendToAIExperienceMachine({ type: 'retry' })}
			showRetryButton={retryEnabled(errorInfo)}
			appearance={appearance}
			isAIFC={isInDocumentStreamingEnabled}
		/>
	);
}
